import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch
} from '@mui/material'
import {
  Edit,
  Delete,
  Search,
  Add,
  Visibility,
  Business
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const GestionesList = () => {
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const [gestiones, setGestiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedGestion, setSelectedGestion] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    activa: true
  })

  useEffect(() => {
    loadGestiones()
  }, [])

  const loadGestiones = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/gestiones?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGestiones(response.data)
    } catch (error) {
      setError('Error al cargar gestiones: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedGestion(null)
    setFormData({
      nombre: '',
      codigo: '',
      descripcion: '',
      activa: true
    })
    setOpenDialog(true)
  }

  const handleEdit = (gestion) => {
    setSelectedGestion(gestion)
    setFormData({
      nombre: gestion.nombre,
      codigo: gestion.codigo,
      descripcion: gestion.descripcion,
      activa: gestion.activa
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      if (selectedGestion) {
        // Editar
        await axios.put(`/api/gestiones/${selectedGestion.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
      } else {
        // Crear
        await axios.post('/api/gestiones', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
      }
      
      setOpenDialog(false)
      loadGestiones()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (gestion) => {
    if (!window.confirm(`¿Está seguro de eliminar la gestión "${gestion.nombre}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/gestiones/${gestion.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      loadGestiones()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const filteredGestiones = gestiones.filter(gestion =>
    gestion.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gestion.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando gestiones...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Business sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Gestiones del Sistema
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Nueva Gestión
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar gestiones por nombre o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Procedimientos</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGestiones.map((gestion) => (
              <TableRow key={gestion.id} hover>
                <TableCell>
                  <Chip
                    label={gestion.codigo}
                    sx={{
                      backgroundColor: gestion.color || '#64B5F6',
                      color: '#fff',
                      fontWeight: 'bold',
                      border: `2px solid ${gestion.color || '#64B5F6'}`
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {gestion.nombre}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {gestion.descripcion}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      Total: {gestion.total_procedimientos || 0}
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      Aprobados: {gestion.procedimientos_aprobados || 0}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={gestion.activa ? 'Activa' : 'Inactiva'}
                    color={gestion.activa ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/gestiones/${gestion.id}`)}
                      title="Ver Detalle"
                      color="primary"
                    >
                      <Visibility />
                    </IconButton>
                    {isAdmin && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(gestion)}
                          title="Editar"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(gestion)}
                          title="Eliminar"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredGestiones.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron gestiones
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedGestion ? 'Editar Gestión' : 'Nueva Gestión'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Código"
              value={formData.codigo}
              onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activa}
                  onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
                />
              }
              label="Gestión activa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GestionesList